import {
  TaskTrackerBoard,
  TaskTrackerBoardAccessMember,
  TaskTrackerBoardAccessSettings,
  TaskTrackerBoardCard,
  TaskTrackerBoardCardPerformer,
  TaskTrackerBoardCardUser,
  TaskTrackerBoardColumn,
  TaskTrackerBoardDetails,
  TaskTrackerBoardLaneColumn,
  TaskTrackerBoardLaneItem,
  TaskTrackerBoardLaneSummary,
} from '../store/taskTracker.types'
import { sortBoardItemsByPosition, extractColumnsFromBoardItems } from '../lib/taskTrackerBoardItems.utils'
import { mapBoardStickerFiltersFromApi } from '../lib/taskTrackerBoard.stickerFilters.utils'
import {
  ApiBoard,
  ApiBoardAccessMember,
  ApiBoardAccessMemberUser,
  ApiBoardAccessResponse,
  ApiBoardCard,
  ApiBoardCardPerformer,
  ApiBoardCardUser,
  ApiBoardColumn,
  ApiBoardDetails,
  ApiBoardItem,
  ApiBoardItemColumn,
  ApiBoardItemSummary,
} from './taskTracker.api.types'
import { ensureApiArray } from './taskTracker.api.mappers.utils'
import { mapBoardStickersFromApi, mapTaskStickersFromApi } from './taskTracker.filters.api.mappers'
import { mapTaskTrackerSummaryFromApi } from './taskTracker.summaries.api.mappers'

export const mapBoardFromApi = (item: ApiBoard): TaskTrackerBoard => ({
  id: Number(item.id),
  projectId: Number(item.project_id),
  title: String(item.title ?? '').trim(),
  position: Number(item.position ?? 0),
  hasBoardAccess: item.has_board_access !== false,
})

export const sortBoardsByPosition = (
  boards: TaskTrackerBoard[],
): TaskTrackerBoard[] => {
  if (!boards.some((board) => board.position > 0)) return boards

  return [...boards].sort((left, right) => {
    const leftPosition =
      left.position > 0 ? left.position : Number.MAX_SAFE_INTEGER
    const rightPosition =
      right.position > 0 ? right.position : Number.MAX_SAFE_INTEGER

    return leftPosition - rightPosition || left.id - right.id
  })
}

const mapBoardAccessMemberUserFromApi = (
  item: ApiBoardAccessMemberUser,
): NonNullable<TaskTrackerBoardAccessMember['user']> => ({
  id: Number(item.id),
  name: String(item.name ?? '').trim(),
  email: item.email ?? null,
  phone: item.phone ?? null,
  avatarPath: item.avatar_path ?? null,
})

export const mapBoardAccessMemberFromApi = (
  item: ApiBoardAccessMember,
): TaskTrackerBoardAccessMember => ({
  userId: Number(item.user_id),
  user: item.user ? mapBoardAccessMemberUserFromApi(item.user) : null,
})

export const mapBoardAccessFromApi = (
  item: ApiBoardAccessResponse,
): TaskTrackerBoardAccessSettings => ({
  board: mapBoardFromApi(item.board),
  accessMode:
    item.access_mode === 'selected_members'
      ? 'selected_members'
      : 'all_project_members',
  selectedMembers: (item.selected_members ?? []).map(mapBoardAccessMemberFromApi),
})

const mapBoardCardUserFromApi = (
  item: ApiBoardCardUser | null | undefined,
): TaskTrackerBoardCardUser | null => {
  if (!item) return null

  return {
    id: Number(item.id),
    name: String(item.name ?? '').trim(),
    email: item.email ?? null,
  }
}

const mapBoardCardPerformersFromApi = (
  items: ApiBoardCardPerformer[] | null | undefined,
): TaskTrackerBoardCardPerformer[] =>
  ensureApiArray(items)
    .map((item) => ({
      userId: Number(item.user_id),
      sorting: Number(item.sorting ?? 0),
      user: mapBoardCardUserFromApi(item.user),
    }))
    .filter((item) => Number.isFinite(item.userId) && item.userId > 0)
    .sort((a, b) => a.sorting - b.sorting || a.userId - b.userId)

export const mapBoardCardFromApi = (
  item: ApiBoardCard,
): TaskTrackerBoardCard => {
  let performers = mapBoardCardPerformersFromApi(item.performers)

  if (performers.length === 0 && item.performer) {
    const user = mapBoardCardUserFromApi(item.performer)
    if (user) {
      performers = [
        {
          userId: user.id,
          sorting: 10,
          user,
        },
      ]
    }
  }

  const performerIds = performers.map((performer) => performer.userId)

  return {
    id: Number(item.id ?? item.task_id),
    boardId: Number(item.board_id),
    columnId: Number(item.column_id),
    title: String(item.title ?? '').trim(),
    description: item.description ? String(item.description).trim() : null,
    performerIds,
    performers,
    isCompleted: Boolean(item.is_completed),
    deadline: item.deadline ? String(item.deadline) : null,
    position: Number(item.position ?? 0),
    creatorId:
      item.creator_id !== null && item.creator_id !== undefined
        ? Number(item.creator_id)
        : null,
    performer: performers[0]?.user ?? mapBoardCardUserFromApi(item.performer),
    creator: mapBoardCardUserFromApi(item.creator),
    stickers: mapTaskStickersFromApi(item.stickers),
  }
}

export const mapBoardCardsFromApi = (
  items: ApiBoardCard[] | null | undefined,
): TaskTrackerBoardCard[] => ensureApiArray(items).map(mapBoardCardFromApi)

export const mapBoardColumnFromApi = (
  item: ApiBoardColumn,
): TaskTrackerBoardColumn => {
  const cards = ensureApiArray(item.cards).map(mapBoardCardFromApi)

  return {
    id: Number(item.id),
    boardId: Number(item.board_id),
    title: String(item.title ?? '').trim(),
    color: String(item.color ?? '').trim(),
    position: Number(item.position ?? 0),
    cards,
    cardsCount: cards.length,
  }
}

const mapBoardLaneColumnFromApi = (
  item: ApiBoardItemColumn | ApiBoardColumn,
): TaskTrackerBoardLaneColumn => {
  const column = mapBoardColumnFromApi(item)
  const cardsTotalCount = Number(
    item.cards_total_count ?? column.cards.length,
  )

  return {
    ...column,
    type: 'column',
    cardsCount: cardsTotalCount,
    cardsLimit:
      item.cards_limit != null ? Number(item.cards_limit) : undefined,
    cardsTotalCount,
    cardsHasMore: Boolean(item.cards_has_more),
  }
}

const mapBoardLaneSummaryFromApi = (
  item: ApiBoardItemSummary,
): TaskTrackerBoardLaneSummary => {
  const cards = ensureApiArray(item.cards).map(mapBoardCardFromApi)
  const summaryMeta = mapTaskTrackerSummaryFromApi({
    id: item.id,
    user_id: item.user_id,
    title: item.title,
    columns_config: item.columns_config ?? [],
    filters_config: item.filters_config ?? [],
    sorting_config: item.sorting_config ?? null,
    created_at: item.created_at ?? 0,
    updated_at: item.updated_at ?? 0,
    user: null,
  })

  return {
    type: 'summary',
    id: Number(item.id),
    boardId: Number(item.board_id),
    title: String(item.title ?? '').trim(),
    position: Number(item.position ?? 0),
    userId: summaryMeta.userId,
    columnsConfig: summaryMeta.columnsConfig,
    filtersConfig: summaryMeta.filtersConfig,
    sortingConfig: summaryMeta.sortingConfig,
    createdAt: summaryMeta.createdAt,
    updatedAt: summaryMeta.updatedAt,
    cards,
    cardsCount: cards.length,
    cardsLimit:
      item.cards_limit != null ? Number(item.cards_limit) : undefined,
    cardsTotalCount: Number(item.cards_total_count ?? cards.length),
    cardsHasMore: Boolean(item.cards_has_more),
    nextCursor: item.next_cursor ?? null,
  }
}

export const mapBoardItemFromApi = (item: ApiBoardItem): TaskTrackerBoardLaneItem => {
  if (item.type === 'summary') {
    return mapBoardLaneSummaryFromApi(item)
  }

  return mapBoardLaneColumnFromApi(item)
}

export const mapBoardItemsFromApi = (
  items: ApiBoardItem[] | null | undefined,
): TaskTrackerBoardLaneItem[] =>
  sortBoardItemsByPosition(ensureApiArray(items).map(mapBoardItemFromApi))

const buildBoardItemsFallbackFromApi = (
  item: ApiBoardDetails,
): TaskTrackerBoardLaneItem[] => {
  const columns = mapBoardColumnsFromApi(item.columns ?? [])

  return columns.map((column) => ({
    ...column,
    type: 'column' as const,
    cardsHasMore: Boolean(item.cards_has_more),
    cardsTotalCount: column.cardsCount,
  }))
}

export const mapBoardColumnsFromApi = (
  items: ApiBoardColumn[] | null | undefined,
): TaskTrackerBoardColumn[] =>
  ensureApiArray(items)
    .map(mapBoardColumnFromApi)
    .sort((a, b) => a.position - b.position)

export const mapBoardDetailsFromApi = (
  item: ApiBoardDetails,
): TaskTrackerBoardDetails => {
  const boardItems =
    item.board_items != null && item.board_items.length > 0
      ? mapBoardItemsFromApi(item.board_items)
      : buildBoardItemsFallbackFromApi(item)

  return {
    board: mapBoardFromApi(item.board),
    boardItems,
    columns: extractColumnsFromBoardItems(boardItems),
    stickers: mapBoardStickersFromApi(item.stickers),
    cardsLimit: Number(item.cards_limit ?? item.filters?.limit ?? 0),
    cardsTotalCount: Number(item.cards_total_count ?? 0),
    cardsHasMore: Boolean(item.cards_has_more),
    appliedStickerFilters: mapBoardStickerFiltersFromApi(
      item.filters?.sticker_filters,
    ),
  }
}

export const mergeBoardColumnsOrderFromApi = (
  previousColumns: TaskTrackerBoardColumn[],
  apiColumns: TaskTrackerBoardColumn[],
): TaskTrackerBoardColumn[] => {
  const previousById = new Map(
    previousColumns.map((column) => [column.id, column]),
  )

  return apiColumns
    .map((column) => {
      const previous = previousById.get(column.id)
      if (!previous) return column

      return {
        ...column,
        cards: previous.cards,
        cardsCount: previous.cardsCount,
      }
    })
    .sort((a, b) => a.position - b.position)
}

export const mergeBoardItemsOrderFromApi = (
  previousItems: TaskTrackerBoardLaneItem[],
  apiItems: TaskTrackerBoardLaneItem[],
): TaskTrackerBoardLaneItem[] => {
  const previousByKey = new Map(
    previousItems.map((item) => [`${item.type}:${item.id}`, item]),
  )

  return sortBoardItemsByPosition(
    apiItems.map((item) => {
      const previous = previousByKey.get(`${item.type}:${item.id}`)
      if (!previous) return item

      if (item.type === 'column' && previous.type === 'column') {
        return {
          ...item,
          cards: previous.cards,
          cardsCount: previous.cardsCount,
          cardsHasMore: previous.cardsHasMore ?? item.cardsHasMore,
          cardsTotalCount: previous.cardsTotalCount ?? item.cardsTotalCount,
        }
      }

      if (item.type === 'summary' && previous.type === 'summary') {
        return {
          ...item,
          cards: previous.cards,
          cardsCount: previous.cardsCount,
          cardsHasMore: previous.cardsHasMore,
          cardsTotalCount: previous.cardsTotalCount,
          nextCursor: previous.nextCursor,
        }
      }

      return item
    }),
  )
}
